// src/pages/portal/MenuDiario.js
import React, { useState, useEffect } from "react";
import { supabase } from "../../supabaseClient";
import { useAuth } from "../../AuthContext";
import Lightbox from "yet-another-react-lightbox";
import "yet-another-react-lightbox/styles.css";
import { Loader2, Images, Calendar } from "lucide-react";

const MenuDiario = () => {
  const { userData, userType, loading: authLoading } = useAuth();
  const [categories, setCategories] = useState([]);
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [lightboxIndex, setLightboxIndex] = useState(-1);

  // Obtener la fecha de hoy formateada para mostrarla
  const today = new Date();
  const todayFormatted = today.toLocaleDateString('es-SV', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    timeZone: 'America/El_Salvador' // Asegura la zona horaria correcta
  });

  useEffect(() => {
    const fetchData = async () => {
      // Espera a que la autenticación termine y userData esté disponible
      if (authLoading || !userData) {
        return;
      }

      setLoading(true);

      let schoolId = null;
      // --- INICIO DE CORRECCIÓN ---
      // Determinar schoolId basado en userType
      if (userType === 'student') {
        schoolId = userData.school_id;
      } else if (userType === 'teacher') { // Añadido caso para 'teacher'
        schoolId = userData.school_id; // Asume que userData del maestro tiene school_id
      } else if (userType === 'parent' && userData.students && userData.students.length > 0) {
        // Asegúrate de que el padre tenga estudiantes asociados antes de acceder
        schoolId = userData.students[0].school_id;
      }
      // --- FIN DE CORRECCIÓN ---

      if (!schoolId) {
        setLoading(false);
        console.error("No se pudo determinar el colegio del usuario.");
        // Considera mostrar un mensaje al usuario aquí
        return;
      }

      // Calcular inicio y fin del día en la zona horaria local
      const todayStart = new Date();
      todayStart.setHours(0, 0, 0, 0);

      const todayEnd = new Date();
      todayEnd.setHours(23, 59, 59, 999);

      // Convertir a formato ISO que Supabase entiende
      const todayStartISO = todayStart.toISOString();
      const todayEndISO = todayEnd.toISOString();

      try {
        const [categoriesRes, imagesRes] = await Promise.all([
          supabase.from("daily_menu_categories").select("*").eq('school_id', schoolId).order("name"),
          // Filtrar imágenes por el rango de fecha de HOY
          supabase
            .from("daily_menu_images")
            .select("*")
            .eq('school_id', schoolId)
            .gte('created_at', todayStartISO)
            .lte('created_at', todayEndISO)
            .order("created_at", { ascending: false }),
        ]);

        if (categoriesRes.error) throw categoriesRes.error;
        if (imagesRes.error) throw imagesRes.error;

        setCategories(categoriesRes.data || []);
        setImages(imagesRes.data || []);

      } catch (error) {
         console.error("Error fetching menu data:", error);
         // Podrías mostrar un mensaje de error al usuario
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [userData, userType, authLoading]); // Dependencias correctas

  const filteredImages =
    selectedCategory === "all"
      ? images
      : images.filter((image) => image.category_id === selectedCategory);

  const slides = filteredImages.map((img) => ({
    src: img.image_url,
    title: img.title,
  }));

  // Muestra loader si la autenticación aún está cargando
  if (authLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Loader2 className="animate-spin text-orange-500" size={48} />
      </div>
    );
  }

  // Renderizado del componente
  return (
    <div className="bg-white rounded-lg shadow-md overflow-hidden"> {/* Added overflow-hidden */}
      {/* Encabezado */}
      <div className="relative bg-gray-800 rounded-t-lg">
         {/* Opcional: Imagen de fondo si la tienes */}
         {/* <img src={backgroundImage} alt="" className="absolute inset-0 w-full h-full object-cover opacity-30"/> */}
        <div className="relative z-10 text-center py-14 px-6">
          <Images className="mx-auto mb-6 text-orange-400 w-14 h-14" />
          <h1 className="text-4xl font-extrabold text-white tracking-wide">Menú del Día</h1>
          <p className="mt-4 text-lg text-gray-200 flex items-center justify-center gap-2">
            <Calendar size={20} />
            {todayFormatted}
          </p>
        </div>
      </div>

      {/* Filtros de Categoría */}
      <div className="py-6 flex justify-center flex-wrap gap-3 px-6 border-b border-gray-200 bg-gray-50"> {/* Added bg-gray-50 */}
        <button
          onClick={() => setSelectedCategory("all")}
          className={`px-5 py-2 text-sm rounded-full font-semibold transition shadow-sm ${
            selectedCategory === "all"
              ? "bg-orange-500 text-white ring-2 ring-orange-300 ring-offset-1" // Added ring for focus
              : "bg-white text-gray-700 hover:bg-gray-100" // Changed non-selected style
          }`}
        >
          Todos
        </button>
        {categories.map((category) => (
          <button
            key={category.id}
            onClick={() => setSelectedCategory(category.id)}
            className={`px-5 py-2 text-sm rounded-full font-semibold transition shadow-sm ${
              selectedCategory === category.id
                ? "bg-orange-500 text-white ring-2 ring-orange-300 ring-offset-1" // Added ring for focus
                : "bg-white text-gray-700 hover:bg-gray-100" // Changed non-selected style
            }`}
          >
            {category.name}
          </button>
        ))}
      </div>

      {/* Galería de Imágenes */}
      <div className="max-w-7xl mx-auto px-6 pt-8 pb-12"> {/* Adjusted padding */}
        {loading ? ( // Muestra loader mientras carga las imágenes
          <div className="flex justify-center items-center h-64">
            <Loader2 className="animate-spin text-orange-500" size={48} />
          </div>
        ) : filteredImages.length === 0 ? ( // Mensaje si no hay imágenes
          <div className="text-center py-16">
             <p className="text-xl text-gray-500 font-semibold"> {/* Larger text */}
              El menú para hoy aún no ha sido publicado.
            </p>
            <p className="text-gray-400 text-sm mt-2">Por favor, inténtalo de nuevo más tarde.</p>
          </div>
        ) : ( // Muestra la cuadrícula de imágenes
          <div
            className={`grid gap-6 grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4`}
          >
            {filteredImages.map((image, idx) => (
              <div
                key={image.id}
                className="group relative cursor-pointer overflow-hidden rounded-xl shadow-lg border border-gray-100" // Added border
                onClick={() => setLightboxIndex(idx)}
              >
                <img
                  src={image.image_url}
                  alt={image.title || "Imagen del menu"}
                  className="w-full h-56 object-cover transform transition duration-500 group-hover:scale-110" // Simplified hover effect
                />
                {/* Overlay con título opcional */}
                {image.title && (
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 via-black/40 to-transparent p-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300"> {/* Gradient overlay */}
                    <p className="text-sm font-semibold text-white truncate">
                      {image.title}
                    </p>
                  </div>
                )}
                 {/* Indicador visual de hover (opcional) */}
                <div className="absolute inset-0 bg-black opacity-0 group-hover:opacity-10 transition-opacity duration-300"></div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Lightbox para ver imágenes en grande */}
      <Lightbox
        slides={slides}
        open={lightboxIndex >= 0}
        index={lightboxIndex}
        close={() => setLightboxIndex(-1)}
         // Puedes añadir más configuraciones al Lightbox si lo necesitas
        // Por ejemplo: captions={{ showToggle: true, descriptionTextAlign: 'center' }}
      />
    </div>
  );
};

export default MenuDiario;